import React, { useState } from 'react';

import { Collection, Notification as NotificationModel, NotificationManager } from 'api-client';
import { GearButton } from 'components/common';
import { FilterBar } from 'components/common/filters';
import NotificationModal from 'components/common/modals/NotificationModal';
import { useWindowWidth } from 'components/pages/detailed-project/hooks';
import { Pagination } from 'components/pagination';
import { createNotificationManager, createProfileManager } from 'di';
import { useErrorCallback } from 'hooks';
import { useDispatch } from 'react-redux';
import { set as setProfile } from 'store/profile';
import { DataCollection } from 'utils';

import Notification from '../Notification';
import NotificationsContextProvider, {
  NotificationsFilter,
  NotificationsFilterContextType,
} from '../NotificationsFilterContext';
import styles from './NotificationsList.module.scss';

type NotificationsTypeOption = {
  id: string;
  label: string;
  filter: NotificationsFilter;
};

const TYPE_OPTIONS: NotificationsTypeOption[] = [
  { id: 'all', label: 'Все', filter: { isRead: true } },
  { id: 'important', label: 'Важные', filter: { isImportant: true } },
];

const NotificationsList = () => {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<DataCollection<NotificationModel>>(
    new DataCollection()
  );
  const [filter, setFilter] = useState<NotificationsFilter>({ isRead: true });
  const isMobile = useWindowWidth(992);
  const handleError = useErrorCallback();
  const dispatch = useDispatch();

  const markAsRead = async (manager: NotificationManager, items: NotificationModel[]) => {
    const unreadUuids = items.filter((item) => !item.isRead).map((item) => item.uuid);
    if (unreadUuids.length === 0) {
      return;
    }

    try {
      await manager.markNotificationRead(unreadUuids);
      const profileManager = await createProfileManager();
      dispatch(setProfile(await profileManager.get()));
    } catch (error: any) {
      await handleError(error);
    }
  };

  const loadData = async (page: number, perPage: number, filter: NotificationsFilter) => {
    setLoading(true);
    try {
      const manager = await createNotificationManager();
      const list = await manager.getList(filter, page, perPage);
      const collection = new Collection(list.items, list.totalCount, list.totalCount);
      const dataCollection = new DataCollection<NotificationModel>();
      dataCollection.setCollection(collection);
      setNotifications(dataCollection);
      await markAsRead(manager, list.items);
    } catch (error: any) {
      await handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const isEmpty = notifications.items.length === 0;

  const value: NotificationsFilterContextType = {
    filter,
    setFilter,
  };

  const [editModalOpened, setEditModalOpened] = useState(false);

  const isOptionActive = (option: NotificationsTypeOption): boolean =>
    filter.isRead === option.filter.isRead && filter.isImportant === option.filter.isImportant;

  return (
    <NotificationsContextProvider props={value}>
      <div className={styles.root}>
        <Pagination
          onLoad={loadData}
          count={notifications.count}
          totalCount={notifications.totalCount}
          filterObject={filter}
          initialChangeFilter={(value: Partial<NotificationsFilter>) =>
            setFilter({ ...filter, ...value })
          }
        >
          <div className={styles.list}>
            <FilterBar className={styles.toolbar}>
              <FilterBar.Pills>
                {TYPE_OPTIONS.map((option) => (
                  <FilterBar.Pill
                    key={option.id}
                    active={isOptionActive(option)}
                    onClick={() => setFilter({ ...option.filter })}
                  >
                    {option.label}
                  </FilterBar.Pill>
                ))}
              </FilterBar.Pills>
              {isMobile && (
                <FilterBar.Actions>
                  <div className={styles.settings}>
                    <GearButton onClick={() => setEditModalOpened(true)} />
                    <NotificationModal
                      active={editModalOpened}
                      onClose={() => setEditModalOpened(false)}
                    />
                  </div>
                </FilterBar.Actions>
              )}
            </FilterBar>

            <Pagination.Content loading={loading}>
              {isEmpty ? (
                <div className={styles.empty}>Уведомления не найдены</div>
              ) : (
                <div className={styles.items}>
                  {notifications.items.map((notification) => (
                    <Notification notification={notification} key={notification.uuid} />
                  ))}
                </div>
              )}
            </Pagination.Content>
          </div>
          <Pagination.Paginator />
        </Pagination>
      </div>
    </NotificationsContextProvider>
  );
};

export default NotificationsList;
